import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useItemCards } from './ItemCardContext';
import { isPlainLeftClick } from '../../utils/navClick';
import { itemHashHref } from '../../utils/itemId';

const isMobile = () => typeof window !== 'undefined' && window.innerWidth <= 640;

/**
 * Wraps a surface that renders an item id/title so it gains the advanced tooltip.
 *
 *  - Desktop: hover → mini peek (→ expands to the full card when the pointer moves
 *    into it). Clicking the id is a HYPERLINK → navigates to the item (#183).
 *  - Mobile: tapping the id opens the tooltip card (no hover available); a small
 *    "open link" ↗ icon next to the id navigates instead.
 *  - Keyboard: Enter opens the card (so non-pointer users can reach quick-edit).
 *
 * `item` carries whatever is known ({id, display_id, title, type, status}); the
 * card fetches the full record itself.
 */
export default function ItemTooltip({ item, children, openDelay = 320 }) {
  const { showMini, hideMini, openWindow, navigate } = useItemCards();
  const timer = useRef(null);
  const elRef = useRef(null);
  const itemId = item?.id || item?.display_id;

  // If this trigger unmounts while a hover-open delay is pending (e.g. a data
  // refresh — TreeSync auto-refresh, a relationship-list re-fetch — replaces this
  // node before the timer fires), the callback still ran with `elRef.current`
  // now null: `elRef.current?.getBoundingClientRect()` silently evaluated to
  // `undefined`, so `showMini(item, undefined)` set a rect-less mini that
  // ItemCardLayer's `if (mini && mini.rect)` guard never renders — hover simply
  // did nothing, no error. Clear the pending timer on unmount instead.
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  // Deep-link via the `#item=<id>` hash the tree page already listens for
  // (RequirementLink / ActivityFeed use the same mechanism) instead of pushing
  // a standalone `/item/:id` route — that kept the tree mounted, this used to
  // navigate away from it and lose the tree entirely. If we're not already on
  // a page that can render the tree, hop to it first; the hash is read on
  // mount so ordering doesn't matter.
  const go = () => {
    if (!itemId) return;
    if (window.location.pathname !== '/') navigate('/');
    window.location.hash = `item=${itemId}`;
  };
  const hrefFor = () => itemHashHref(itemId);

  const enter = () => {
    if (isMobile()) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => showMini(item, elRef.current?.getBoundingClientRect()), openDelay);
  };
  const leave = () => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    if (!isMobile()) hideMini();
  };
  const onClick = (e) => {
    // Issue #339: a middle-click / ctrl-click / cmd-click / shift-click (or a
    // real right-click) must fall through to the browser's native "open in a
    // new tab/window" (or context menu) instead of being hijacked into the
    // in-place SPA hash-navigation below — only a plain left click does that.
    if (!isPlainLeftClick(e)) return;
    e.preventDefault();
    e.stopPropagation();
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    if (isMobile()) openWindow(item, elRef.current?.getBoundingClientRect(), { pinned: true });
    else go(); // desktop click = hyperlink
  };
  const onKey = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); openWindow(item, elRef.current?.getBoundingClientRect(), { pinned: true }); }
  };

  // A real <a href> (not a <span role="link">) so a middle-click (or
  // ctrl/cmd-click) opens the item in a new tab via the browser's native
  // behaviour — the new tab boots the app fresh and deep-links to `#item=id`,
  // landing on the same tree position. Left-click is intercepted so it stays
  // an in-place SPA navigation instead of a real hash-only jump.
  const trigger = (
    <a
      ref={elRef}
      href={hrefFor()}
      onMouseEnter={enter}
      onMouseLeave={leave}
      onFocus={enter}
      onBlur={leave}
      onClick={onClick}
      onKeyDown={onKey}
      aria-label={`Open ${item?.display_id || item?.title || 'item'}`}
      style={{ cursor: 'pointer', color: 'inherit', textDecoration: 'none' }}
    >
      {children}
    </a>
  );

  // On mobile the id press opens the tooltip, so offer a small explicit link icon.
  if (isMobile()) {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
        {trigger}
        <button
          type="button"
          className="ic-openlink"
          aria-label={`Open ${item?.display_id || 'item'} page`}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); go(); }}
        >↗</button>
      </span>
    );
  }
  return trigger;
}

ItemTooltip.propTypes = {
  item: PropTypes.object.isRequired,
  children: PropTypes.node,
  openDelay: PropTypes.number,
};
